import React, { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "https://manavastralu-backend.onrender.com";

export default function AddProduct() {
  const [form, setForm] = useState({
    name: "", description: "", price: "", originalPrice: "",
    fabric: "", category: "", stock: "10",
  });

  // ✅ NEW — this is the actual root cause fix. This page never had a
  // per-size stock UI at all — just a single flat "Default Stock"
  // number and a sizes-names-only text field, which is why every
  // product created here ends up with NO sizeStock in MongoDB
  // (confirmed directly in your Atlas screenshot). sizeRows replaces
  // the old plain "sizes" text field with real size + stock pairs.
  const [sizeRows, setSizeRows] = useState([{ size: "", stock: "5" }]);

  // Up to 4 images (main product photos)
  const [images, setImages] = useState([null, null, null, null]);

  // Colour variants: [{ name, stock, imageFile }]
  const [variants, setVariants] = useState([{ name: "", stock: "10", imageFile: null }]);

  const [loading, setLoading] = useState(false);

  const handleField = e =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleImage = (i, file) =>
    setImages(prev => { const a = [...prev]; a[i] = file; return a; });

  const addVariant = () =>
    setVariants(v => [...v, { name: "", stock: "10", imageFile: null }]);

  const updateVariant = (i, key, val) =>
    setVariants(v => v.map((x, idx) => idx === i ? { ...x, [key]: val } : x));

  const removeVariant = (i) =>
    setVariants(v => v.filter((_, idx) => idx !== i));

  // ✅ NEW — size row handlers, same pattern used elsewhere in the
  // admin panel so the behavior is consistent across the app.
  const updateSizeRow = (i, key, val) =>
    setSizeRows(rows => rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const addSizeRow = () =>
    setSizeRows(rows => [...rows, { size: "", stock: "5" }]);

  const removeSizeRow = (i) =>
    setSizeRows(rows => rows.filter((_, idx) => idx !== i));

  const sizeRowsTotal = sizeRows.reduce((s, r) => s + (Number(r.stock) || 0), 0);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.price) { alert("Name and Price are required"); return; }

    // ✅ Build sizes + sizeStock from sizeRows — this is what was
    // completely missing before.
    const validSizeRows = sizeRows.filter(r => r.size.trim() && r.stock !== "");
    const sizes         = validSizeRows.map(r => r.size.trim());
    const sizeStock      = {};
    validSizeRows.forEach(r => { sizeStock[r.size.trim()] = Number(r.stock) || 0; });
    const totalFromSizes = Object.values(sizeStock).reduce((s, v) => s + v, 0);

    setLoading(true);
    try {
      const data = new FormData();

      // Basic fields — sizes/stock handled separately below now
      Object.entries(form).forEach(([k, v]) => {
        if (k === "stock") return; // replaced by totalFromSizes when sizes exist
        data.append(k, v);
      });

      // ✅ Sizes + real per-size stock
      data.append("sizes", JSON.stringify(sizes));
      data.append("sizeStock", JSON.stringify(sizeStock));
      // Total stock: sum of per-size stock when sizes exist, otherwise
      // fall back to the flat "Default Stock" field (e.g. accessories
      // with no size variants at all).
      data.append("stock", String(sizes.length > 0 ? totalFromSizes : (Number(form.stock) || 0)));

      // Main images
      images.filter(Boolean).forEach(f => data.append("images", f));

      // Colour variants — send as JSON meta + separate image files
      const variantMeta = variants.map((v, i) => ({
        name: v.name,
        stock: Number(v.stock),
        imageIndex: i, // matches the variantImages[] order on the server
      }));
      data.append("colorVariants", JSON.stringify(variantMeta));
      variants.forEach(v => {
        if (v.imageFile) data.append("variantImages", v.imageFile);
      });

      const res = await axios.post(`${API}/api/products`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ✅ Confirms exactly what was saved — check this in the console
      // against MongoDB Atlas if anything ever looks off again.
      console.log("✅ Product created:", res.data);
      console.log("   sizeStock sent:", sizeStock);
      console.log("   sizeStock saved (from server response):", res.data.sizeStock);

      alert(`Product added! ${sizes.length > 0 ? `${sizes.length} sizes, ${totalFromSizes} total pcs` : `${form.stock} pcs`}`);
      setForm({ name:"", description:"", price:"", originalPrice:"", fabric:"", category:"", stock:"10" });
      setSizeRows([{ size:"", stock:"5" }]);
      setImages([null, null, null, null]);
      setVariants([{ name: "", stock: "10", imageFile: null }]);
    } catch (err) {
      alert("Error: " + (err.response?.data?.error || err.message));
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <h2 style={styles.h2}>Add Product</h2>

      {/* ── Basic fields (stock + sizes removed from this list — see below) */}
      {[
        ["name", "Product Name *"],
        ["price", "Price (₹) *"],
        ["originalPrice", "Original Price (₹) — optional"],
        ["fabric", "Fabric"],
        ["category", "Category (Sarees / Dresses / Lehengas / Kurtis)"],
      ].map(([key, label]) => (
        <label key={key} style={styles.label}>
          {label}
          <input
            name={key}
            value={form[key]}
            onChange={handleField}
            style={styles.input}
            type={["price","originalPrice"].includes(key) ? "number" : "text"}
          />
        </label>
      ))}

      <label style={styles.label}>
        Description
        <textarea name="description" value={form.description} onChange={handleField} style={{ ...styles.input, height: 80 }} />
      </label>

      {/* ── ✅ NEW — Sizes & Stock Per Size (replaces the old sizes-names-only field) */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Sizes &amp; Stock Per Size</legend>
        <p style={styles.hint}>
          Add each size this product comes in, with how many pieces are actually
          available for that size. Leave empty if this product has no sizes.
        </p>

        {sizeRows.map((row, i) => (
          <div key={i} style={styles.variantRow}>
            <input
              placeholder="Size (e.g. XS, S, M, L, XL)"
              value={row.size}
              onChange={e => updateSizeRow(i, "size", e.target.value)}
              style={{ ...styles.input, flex: 1 }}
            />
            <input
              type="number" min="0" placeholder="Stock"
              value={row.stock}
              onChange={e => updateSizeRow(i, "stock", e.target.value)}
              style={{ ...styles.input, width: 90 }}
            />
            <span style={{ fontSize: 12, color: "#888" }}>pcs</span>
            {sizeRows.length > 1 && (
              <button type="button" onClick={() => removeSizeRow(i)} style={styles.removeBtn}>✕</button>
            )}
          </div>
        ))}

        <button type="button" onClick={addSizeRow} style={styles.addVariantBtn}>
          + Add Another Size
        </button>

        {sizeRows.some(r => r.size && r.stock) && (
          <div style={styles.totalBox}>
            📦 Total stock: <strong>{sizeRowsTotal} pcs</strong> across{" "}
            <strong>{sizeRows.filter(r=>r.size).length}</strong> size(s)
          </div>
        )}
      </fieldset>

      {/* ── Fallback stock — only used if no sizes are entered above */}
      {!sizeRows.some(r => r.size.trim()) && (
        <label style={styles.label}>
          Default Stock (used only if this product has no sizes)
          <input
            name="stock"
            type="number"
            value={form.stock}
            onChange={handleField}
            style={styles.input}
          />
        </label>
      )}

      {/* ── Main images */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Main Product Images (up to 4)</legend>
        {images.map((_, i) => (
          <label key={i} style={styles.label}>
            Image {i + 1}
            <input
              type="file" accept="image/*"
              onChange={e => handleImage(i, e.target.files[0])}
              style={styles.fileInput}
            />
          </label>
        ))}
      </fieldset>

      {/* ── Colour variants */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Colour Variants</legend>
        <p style={styles.hint}>Each variant gets its own image and stock. Leave empty to skip.</p>

        {variants.map((v, i) => (
          <div key={i} style={styles.variantRow}>
            <input
              placeholder="Colour name (e.g. blue)"
              value={v.name}
              onChange={e => updateVariant(i, "name", e.target.value)}
              style={{ ...styles.input, flex: 1 }}
            />
            <input
              type="number" placeholder="Stock"
              value={v.stock}
              onChange={e => updateVariant(i, "stock", e.target.value)}
              style={{ ...styles.input, width: 80 }}
            />
            <input
              type="file" accept="image/*"
              onChange={e => updateVariant(i, "imageFile", e.target.files[0])}
              style={styles.fileInput}
            />
            {variants.length > 1 && (
              <button type="button" onClick={() => removeVariant(i)} style={styles.removeBtn}>✕</button>
            )}
          </div>
        ))}

        <button type="button" onClick={addVariant} style={styles.addVariantBtn}>
          + Add Another Colour
        </button>
      </fieldset>

      <button type="submit" disabled={loading} style={styles.submitBtn}>
        {loading ? "Uploading…" : "Add Product"}
      </button>
    </form>
  );
}

const styles = {
  form:          { maxWidth: 640, margin: "0 auto", padding: 32, fontFamily: "'Segoe UI', sans-serif" },
  h2:            { color: "#8b4513", fontFamily: "Georgia,serif", marginBottom: 24 },
  label:         { display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.5px" },
  input:         { display: "block", width: "100%", padding: "9px 12px", fontSize: 14, border: "1px solid #ccc", borderRadius: 4, marginTop: 4, boxSizing: "border-box" },
  fileInput:     { display: "block", marginTop: 4, fontSize: 13 },
  fieldset:      { border: "1px solid #e0cfc0", borderRadius: 6, padding: 20, marginBottom: 24 },
  legend:        { fontSize: 13, fontWeight: 700, color: "#8b4513", padding: "0 8px" },
  hint:          { fontSize: 12, color: "#888", marginTop: 0, marginBottom: 16 },
  variantRow:    { display: "flex", gap: 10, alignItems: "center", marginBottom: 14 },
  removeBtn:     { background: "none", border: "none", color: "#c00", fontSize: 18, cursor: "pointer" },
  addVariantBtn: { background: "none", border: "1px dashed #c2185b", color: "#c2185b", padding: "7px 16px", borderRadius: 4, cursor: "pointer", fontSize: 13, marginTop: 4 },
  totalBox:      { marginTop: 12, fontSize: 13, color: "#2e7d32", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 4, padding: "8px 12px" },
  submitBtn:     { background: "#8b4513", color: "#fff", border: "none", padding: "13px 32px", fontSize: 14, fontWeight: 700, borderRadius: 4, cursor: "pointer", width: "100%" },
};